import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { Fragment } from 'react'
import { HTTP_CAMPAIGN_TOKEN_HEADER } from '@captaincause/lib/api/headers'
import { looksLikeABot, looksLikeALocale } from '@captaincause/lib/api/path'
import { useNextLocale } from '@captaincause/lib/useNextLocale'
import { Html } from '@captaincause/ui/Html'
import { tm } from '@captaincause/ui/tm'
import { YoutubeIframe } from '@captaincause/ui/YoutubeIframe'
import { getProject } from 'api/projects'
import diftmanSearch from 'assets/diftman-search.webp'
import { NavAndFooterLayout } from 'layouts/NavAndFooter'
import { getBanner } from 'pages/api/banner'
import { PageHead } from 'pages/Head'
import { injectI18n } from 'utils/i18n'
import { CampaignStateBanner } from 'components/CampaignStateBanner'
import type {
  Locales,
  Project as ProjectType,
  ServerGetParams,
} from '@captaincause/types'
import type { NextPageWithLayout } from 'app'
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next'

interface PageProps {
  project: ProjectType
}

export const getServerSideProps: GetServerSideProps<
  PageProps
> = async context => {
  const i18n = await injectI18n(context.locale, [
    'campaign-banner.component',
    'footer.component',
    'metadata',
    'nav.component',
    'projects.page',
  ])
  const { token = undefined } = context.query as {
    token: string
  }

  const banner = await getBanner({ locale: context.locale as Locales })

  const id = context?.params?.slug as string

  if (looksLikeABot(id) || looksLikeALocale(id)) {
    return {
      notFound: true,
    }
  }

  try {
    const project = await getProject({
      id,
      locale: context.locale as Locales,
      headers: token ? [[HTTP_CAMPAIGN_TOKEN_HEADER, token]] : undefined,
    })

    return {
      props: { project, banner, ...i18n },
    }
  } catch (error: unknown) {
    return {
      notFound: true,
    }
  }
}

const NoNews = () => {
  const { t } = useTranslation('projects.page')

  return (
    <div className="mx-auto my-14 flex flex-col items-center">
      <Image src={diftmanSearch} alt="" />
      <h1 className="mt-6 font-default">{t('news.empty')}</h1>
    </div>
  )
}

type InferredPageProps = InferGetServerSidePropsType<typeof getServerSideProps>
const Project: NextPageWithLayout<InferredPageProps> = ({
  project,
}: InferredPageProps) => {
  const locale = useNextLocale()
  const { t } = useTranslation('projects.page')

  return (
    <>
      <PageHead
        head={{
          title: `${t('news.metatitle')} ${project.organization.name}`,
          description: project?.goal || undefined,
        }}
        image={project?.image || undefined}
      />
      <CampaignStateBanner project={project} />
      {project.project_updates.length > 0 ? (
        <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-6 pb-20 md:pt-10 lg:px-20">
          {project.organization.logo ? (
            <Image
              width={88}
              height={88}
              src={project.organization.logo}
              alt=""
              unoptimized
              className="mb-10 h-[88px] w-fit object-contain"
            />
          ) : null}
          <h1 className="mb-10 text-center font-default text-2xl font-bold md:mb-20">
            {project.name}
          </h1>
          <div className="flex flex-col">
            {project.project_updates.map(projectUpdate => (
              <Fragment key={projectUpdate.id}>
                <div
                  id={projectUpdate.id}
                  className={tm(
                    'flex h-auto w-full flex-col gap-4 rounded-lg md:flex-row md:gap-10',
                  )}
                >
                  {projectUpdate.image || projectUpdate.youtube_id ? (
                    <div className="flex flex-1 flex-col">
                      {projectUpdate.image && !projectUpdate.youtube_id ? (
                        <Image
                          src={projectUpdate.image}
                          alt={`${projectUpdate.name} update`}
                          width={400}
                          height={224}
                          className="h-fit w-full rounded-md object-cover object-top md:max-h-[200px] md:max-w-[400px]"
                          loading="lazy"
                        />
                      ) : null}
                      {projectUpdate.youtube_id ? (
                        <YoutubeIframe
                          className="m-0 !w-full md:max-w-[400px]"
                          id={projectUpdate.youtube_id}
                        />
                      ) : null}
                      {projectUpdate.image_legend ? (
                        <span className="mt-4 line-clamp-3 hidden px-6 text-center text-xs text-gray md:block md:max-w-[400px] md:px-4">
                          {projectUpdate.image_legend}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="flex flex-[2] flex-col items-start">
                    <time
                      className="text-sm text-dark-gray"
                      dateTime={projectUpdate.created_at}
                    >
                      {/* TODO: make a Date utils */}
                      {new Date(projectUpdate.created_at).toLocaleDateString(
                        locale,
                        {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                        },
                      )}
                    </time>
                    <h2 className="mb-4 font-default text-1.5xl font-semibold text-black">
                      {projectUpdate.name}
                    </h2>
                    {projectUpdate.body ? (
                      <Html className="text-dark-gray">
                        {projectUpdate.body.replaceAll('<br><br>', '<br>')}
                      </Html>
                    ) : null}
                  </div>
                </div>
                <hr className="my-6 h-px w-full border-none bg-inside last:hidden md:my-10" />
              </Fragment>
            ))}
          </div>
        </div>
      ) : (
        <NoNews />
      )}
    </>
  )
}

Project.Layout = NavAndFooterLayout
Project.pagePropsOnLayout = true

export default Project
